import { useEffect, useState } from "react";

import {
    TextField, Checkbox,
    Select, Button, Modal, AutocompleteMultipleSelect,
    TableMetadata, TableStateWithFilters, useTable, ColumnSortEnum
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook, ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col, } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import {
    searchMunicipalities, searchTowns,
    searchStreets
} from "../../api/Geography/GeographyApi";
import {
    getHouseNumberInstitutionFilteredList,
    getHouseNumberInstitutionListExport,
    saveHouseNumberInstitutionAssignment
} from "../../api/HouseNumberInstitution/HouseNumberInstitutionApi";
import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import { ContentArea, FormCard, InstitutionSelect, ListTable, SearchCard, ToolbarTop } from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import { useAuthCounties } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
    HierarchicalCodeBookPagedFilter, HierarchicalCodeBook,
    GrandParentCodeBookPagedFilter, GreatGrandParentCodeBookPagedFilter,
    ValidationFailure,
    InstitutionCodeBookSearch,
    InstitutionCodeBook
} from "../../models";
import {
    HouseNumberInstitution, HouseNumberInstitutionAssignmentStatus, HouseNumberInstitutionSearch,
    UpdateHouseNumberInstitutionAssignment
} from "../../models/HouseNumberInstitution";
import { HouseNumberSelector } from "../../models/SchoolCandidate/HouseNumberSelector";
import { ClaimType } from "../../models/User";
import { MsgUtils, TableUtils } from "../../utils";


export function HouseNumberInstitutionList() {
    const { t } = useTranslation();
    const auth = useAuth();
    const authCounties = useAuthCounties();
    //STORAGE KEY
    const storageKey = 'HouseNumberInstitutionSearchTableState';
    //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
    const createInitialTableState = () => {
        return TableUtils.CreateInitialTableState<HouseNumberInstitutionSearch>(
            storageKey,
            TableUtils.tableDefaultState(1, ColumnSortEnum.ASC, 50)
        )
    }
    const [initialTableState] = useState<TableStateWithFilters<HouseNumberInstitutionSearch>>(() => createInitialTableState());
    //INIT SEARCH FILTER
    const createInitialHouseNumberInstitutionSearchSearch = (): HouseNumberInstitutionSearch => {
        return {
            countyList: defaultCountyIds,
            municipalityList: [],
            townList: [],
            streetList: [],
            houseNumberFrom: undefined,
            houseNumberTo: undefined,
            houseNumberSelector: undefined,
            institution: undefined,
            assignmentStatus: undefined
        }
    }
    const defaultCountyIds: number[] =
        auth.currentRole?.claimType === ClaimType.County &&
            auth?.currentRole?.locations &&
            (auth.currentRole?.locations?.length ?? 0) > 0
            ? auth.currentRole.locations
            : [] as number[]

    //TABLE DATA
    const defaultTableData = TableUtils.tableDefaultData<HouseNumberInstitution>()
    const [tableData, setTableData] = useState<PagedResult<HouseNumberInstitution>>(defaultTableData);
    //SET SEARCH FILTERS
    const [houseNumberInstitutionSearch, setHouseNumberInstitutionSearch] = useState<HouseNumberInstitutionSearch>(createInitialHouseNumberInstitutionSearchSearch());
    const [tableSearch, setTableSearch] = useState<HouseNumberInstitutionSearch>(createInitialHouseNumberInstitutionSearchSearch());
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [clearFilter, setClearFilter] = useState<boolean>(true);
    //TABLE METADATA
    const tableMetadata: TableMetadata<HouseNumberInstitution> = {
        rowKey: "houseNumberId",
        columns: [
            {
                columnKey: "select",
                header: "",
                accessor: (item: HouseNumberInstitution) => {
                    return (
                        <div>
                            <Checkbox
                                label={""}
                                name={nameof<typeof item>("houseNumberId")}
                                value={item.houseNumberId}
                                checked={item.isSelected}
                                onChange={handleItemCheckedChange}
                            />
                        </div>)
                },
            },
            {
                columnKey: "municipality",
                header: t("common.schoolCandidateModel.municipalityList"),
                accessor: (item: HouseNumberInstitution) => {
                    return (
                        <div className={"text-nowrap"}>
                            {item.municipality}
                        </div>
                    )
                },
            },
            {
                columnKey: "town",
                header: t("common.schoolCandidateModel.townList"),
                accessor: (item: HouseNumberInstitution) => {
                    return (
                        <div className={"text-nowrap"}>
                            {item.town}
                        </div>
                    )
                },
            },
            {
                columnKey: "street",
                header: t("common.schoolCandidateModel.streetList"),
                accessor: (item: HouseNumberInstitution) => {
                    return (
                        <div className={"text-nowrap"}>
                            {item.street}
                        </div>
                    )
                },
            },
            {
                columnKey: "houseNumber",
                header: t("common.schoolCandidateModel.houseNumberSelector") + "/" + t("common.schoolCandidateModel.temporaryAddress"),
                accessor: (item: HouseNumberInstitution) => {
                    return (
                        <div className={"text-nowrap"}>
                            {item.houseNumber}
                        </div>
                    )
                },
            },
            {
                columnKey: "institution",
                header: t("common.schoolCandidateModel.institutionList"),
                accessor: (item: HouseNumberInstitution) => {
                    return (<div className={"text-nowrap"}>
                        {item.institution?.name}
                    </div>)
                },
            },
        ],
        rowToolTip: () => undefined
    };
    //SET TABLE
    const [tableProps, tableState, setFilters] = useTable<HouseNumberInstitutionSearch>(
        {
            metadata: tableMetadata,
            data: tableData!.results,
            pagination: true,
            paginationPage: initialTableState.page,
            paginationPageSize: initialTableState.pageSize as any,
            totalItems: tableData!.rowCount,
            defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
        }
    );
    //FILTER DATA 
    const [filteredInstitutions, setFilteredInstitutions] = useState<InstitutionCodeBook[]>([]);
    const [filteredMunicipalities, setFilteredMunicipalities] = useState<HierarchicalCodeBook[]>([]);
    const [filteredTowns, setFilteredTowns] = useState<HierarchicalCodeBook[]>([]);
    const [filteredStreets, setFilteredStreets] = useState<HierarchicalCodeBook[]>([]);

    //EDIT DATA 
    const createDefaultUpdate = (): UpdateHouseNumberInstitutionAssignment => {
        return {
            institutionId: undefined,
            houseNumberList: []
        };
    };
    const [houseNumberSelectors] = useState<CodeBook[]>(() => {
        return MsgUtils.getEnumToCodeBook(HouseNumberSelector, "houseNumberSelector");
    });
    const [houseNumberAssignmentStatuses] = useState<CodeBook[]>(() => {
        return MsgUtils.getEnumToCodeBook(HouseNumberInstitutionAssignmentStatus, "houseNumberInstitutionAssignmentStatus");
    });
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [updateInstitutionAssignment, setUpdateInstitutionAssignment] = useState<UpdateHouseNumberInstitutionAssignment>(createDefaultUpdate());
    const [addAssignmentEnabled, setAddAssignmentEnabled] = useState<boolean>(false);
    const [clearAssignmentEnabled, setClearAssignmentEnabled] = useState<boolean>(false);


    const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<string>();
    const [confirmUnassign, setConfirmUnassign] = useState<any>()
    const [isClearing, setIsClearing] = useState<boolean>(false);
    const [errors, setErrors] = useState<ValidationFailure[]>()
    const [searchError, setSearchError] = useState<string>()


    useEffect(() => {
        if (clearFilter) {
            setClearFilter(false);
            setFilterChanged(false);
        }
        else
            setFilterChanged(true);
        TableUtils.RemoveSessionStorageItem(storageKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [houseNumberInstitutionSearch]);

    useEffect(() => {
        if (houseNumberInstitutionSearch?.countyList && houseNumberInstitutionSearch.countyList.length === 0) return;
        (async () => {
            var request = TableUtils.GetSearchRequest<HouseNumberInstitutionSearch>(tableSearch, tableState);
            var itemList = await getHouseNumberInstitutionFilteredList(request);
            if (itemList !== null && itemList.results.length > 0) {
                setTableData(itemList);
            }
            else {
                setTableData(defaultTableData);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    //FILTER HANDLERS
    const handleInstitutionFilterChange = async (filter: string) => {
        if (houseNumberInstitutionSearch.countyList.length > 0) {
            const iSearch: InstitutionCodeBookSearch = {
                countyIds: houseNumberInstitutionSearch.countyList,
                onlyActive: false,
                search: filter,
                includeOutsideEnrollmentArea: false,
                includeHandicapped: false,
                includePrivate: false,
                includeSatellite: undefined, // both
            };
            var result = await searchInstitutionNew(iSearch);
            setFilteredInstitutions(result);
        }
    };
    const handleMunicipalityFilterChange = async (filter: string) => {
        const newSearch: HierarchicalCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: houseNumberInstitutionSearch.municipalityList.map(val => val.id),
            parentId: houseNumberInstitutionSearch.countyList,
            search: filter,
        };
        var result = await searchMunicipalities(newSearch);
        setFilteredMunicipalities(result.results);
    };
    const handleTownFilterChange = async (filter: string) => {
        const newSearch: GrandParentCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: houseNumberInstitutionSearch.townList.map(val => val.id),
            parentId: houseNumberInstitutionSearch.municipalityList.map(val => val.id),
            grandParentId: houseNumberInstitutionSearch.countyList,
            search: filter,
        };
        var result = await searchTowns(newSearch);
        setFilteredTowns(result.results);
    };
    const handleStreetFilterChange = async (filter: string) => {
        const newSearch: GreatGrandParentCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: houseNumberInstitutionSearch.streetList.map(val => val.id),
            parentId: houseNumberInstitutionSearch.townList.map(val => val.id),
            grandParentId: houseNumberInstitutionSearch.municipalityList.map(val => val.id),
            greatGrandParentId: houseNumberInstitutionSearch.countyList,
            search: filter,
        };
        var result = await searchStreets(newSearch);
        setFilteredStreets(result.results);
    };
    const handleHouseNumberFromChange = (e: any) => {
        const houseNumberFrom = Number.parseInt(e.target.value);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            houseNumberFrom: !isNaN(houseNumberFrom) ? houseNumberFrom : undefined
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleHouseNumberToChange = (e: any) => {
        const houseNumberTo = Number.parseInt(e.target.value);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            houseNumberTo: !isNaN(houseNumberTo) ? houseNumberTo : undefined
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleAssignedStatusChange = (id: number) => {
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            assignmentStatus: !isNaN(id) ? id : undefined,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleHouseNumberSelectorChange = (e: any) => {
        const houseNumberSelector = Number.parseInt(e.target.value);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            houseNumberSelector: !isNaN(houseNumberSelector) && houseNumberSelector > 0 ? houseNumberSelector : undefined,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleCountyChange = (countyList: number[]) => {
        let municipalityList = houseNumberInstitutionSearch.municipalityList.filter(v => countyList.find(c => c === v.parentId) !== undefined);
        let townList = houseNumberInstitutionSearch.townList.filter(v => municipalityList.find(c => c.id === v.parentId) !== undefined);
        let streetList = houseNumberInstitutionSearch.streetList.filter(v => townList.find(c => c.id === v.parentId) !== undefined);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            countyList: countyList,
            municipalityList: municipalityList,
            townList: townList,
            streetList: streetList,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleMunicipalityChange = (e: any) => {
        const municipalityList = e.target.value as HierarchicalCodeBook[];
        const townList = houseNumberInstitutionSearch.townList.filter(v => municipalityList.find(c => c.id === v.parentId) !== undefined);
        const streetList = houseNumberInstitutionSearch.streetList.filter(v => townList.find(c => c.id === v.parentId) !== undefined);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            municipalityList: municipalityList,
            townList: townList,
            streetList: streetList,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleTownChange = (e: any) => {
        const townList = e.target.value as HierarchicalCodeBook[];
        const streetList = houseNumberInstitutionSearch.streetList.filter(v => townList.find(c => c.id === v.parentId) !== undefined);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            townList: townList,
            streetList: streetList,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleStreetChange = (e: any) => {
        const streetList = e.target.value as HierarchicalCodeBook[];
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            streetList: streetList,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };
    const handleInstitutionChange = (e: any) => {
        //const institutionList = e.target.value as number[];
        const institutionList = e.target.value as CodeBook[];
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            institution: institutionList.length > 0 ? institutionList[0] : undefined,
        };
        setHouseNumberInstitutionSearch(newSearch);
    };


    const handleSearchClicked = (e: any) => {
        setTableSearch(houseNumberInstitutionSearch)
        setFilterChanged(false);
        doSearch();
    };
    const handleClearSearchClicked = () => {
        setClearFilter(true);
        const newSearch: HouseNumberInstitutionSearch = {
            ...houseNumberInstitutionSearch,
            countyList: defaultCountyIds?.length > 0 ? defaultCountyIds : houseNumberInstitutionSearch.countyList,
            municipalityList: [],
            townList: [],
            streetList: [],
            houseNumberFrom: undefined,
            houseNumberTo: undefined,
            houseNumberSelector: undefined,
            institution: undefined,
            assignmentStatus: undefined
        };
        setHouseNumberInstitutionSearch(newSearch);
        setTableSearch(newSearch)
        setFilterChanged(false);
        doSearch();
    };
    const doSearch = () => {
        setSearchError(undefined);
        if (houseNumberInstitutionSearch?.countyList?.length === 0) {
            setSearchError(t("validations.requiredSearchField", { fieldName: 'Županija' }));
            return;
        }
        setFilters({ ...tableSearch });

        const update: UpdateHouseNumberInstitutionAssignment = {
            ...updateInstitutionAssignment,
            houseNumberList: [],
        }
        setUpdateInstitutionAssignment(update);
        setAssignmentButtons(update);
    };
    const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
        await getHouseNumberInstitutionListExport(houseNumberInstitutionSearch, exportType);
    };

    //EDIT HANDLERS
    const handleCloseSaveConfirmation = () => {
        setShowSaveModal(false);
        doSearch();
    };
/*     const handleClearAssignmentsClicked = async () => {
        setIsClearing(true);
        setShowConfirmModal(t("common.houseNumberInstitution.clearConfirmation"));
    };
 */    
    const handleConfirmationClicked = () => {
        setShowConfirmModal(undefined);
        const newUpdate: UpdateHouseNumberInstitutionAssignment = isClearing
            ? {
                ...updateInstitutionAssignment,
                institutionId: undefined,
            }
            : updateInstitutionAssignment;
        handleAssignment(newUpdate);
    };

    const handleClearAssignmentsClicked = async () => {
        const newUpdate: UpdateHouseNumberInstitutionAssignment = {
                ...updateInstitutionAssignment,
                institutionId: undefined,
            };
        var result = await saveHouseNumberInstitutionAssignment(newUpdate);
        if (result) {
            setErrors(result.validationFailures);
            if (result.isValid) {
                setConfirmUnassign("message");
                //doSearch(search);
            }
            doSearch();
        } else {
            setConfirmUnassign("error");
        }
    };
    const handleAddAssignmentsClicked = async () => {
        setIsClearing(false);
        setShowConfirmModal(t("common.houseNumberInstitution.confirmation"));
    };
    const handleAssignment = async (assignment: UpdateHouseNumberInstitutionAssignment) => {
        var result = await saveHouseNumberInstitutionAssignment(assignment);
        if (result) {
            setErrors(result.validationFailures);
            if (result.isValid) {
                setShowSaveModal(true);
                //doSearch(search);
            }
        }
    };
    const handleSelectAllCheckedChange = (e: any) => {
        const isChecked = !selectAll;
        setSelectAll(isChecked);
        const newResults = tableData.results.map(val => { val.isSelected = isChecked; return val; });
        const newTableData: PagedResult<HouseNumberInstitution> = {
            ...tableData,
            results: newResults,
        };
        const newUpdate: UpdateHouseNumberInstitutionAssignment = {
            ...updateInstitutionAssignment,
            houseNumberList: newResults.filter(val => val.isSelected).map(val => val.houseNumberId!),
        }
        setTableData(newTableData);
        setUpdateInstitutionAssignment(newUpdate);
        setAssignmentButtons(newUpdate);
    };
    const handleItemCheckedChange = (e: any) => {
        const id = Number.parseInt(e.target.value);
        const isChecked = e.target.checked as boolean;
        if (id) {
            var item = tableData.results.find(val => val.houseNumberId === id);
            var list = updateInstitutionAssignment.houseNumberList;
            if (isChecked && item) {
                list.push(item.houseNumberId!);
                item!.isSelected = true;
            } else {
                list = list.filter(val => val !== id);
                setSelectAll(false);
                item!.isSelected = false;
            }
            const newUpdate: UpdateHouseNumberInstitutionAssignment = {
                ...updateInstitutionAssignment,
                houseNumberList: list,
            };
            setUpdateInstitutionAssignment(newUpdate);
            setAssignmentButtons(newUpdate);
        }
    };
    const setAssignmentButtons = (u: UpdateHouseNumberInstitutionAssignment) => {
        setAddAssignmentEnabled(u.houseNumberList.length > 0 // house numbers to assign
            && u.institutionId !== undefined // institution to assign
            && tableData.results.filter(row => row.isSelected && row.institution).length === 0 // no already assigned house numbers
        );
        setClearAssignmentEnabled(u.houseNumberList.length > 0 // house numbers to clear
            && tableData.results.filter(row => row.isSelected && row.institution).length > 0 // something to clear
        );
    };
    const handleAssignInstitutionChange = (institutionId?: number) => {
        const newUpdate: UpdateHouseNumberInstitutionAssignment = {
            ...updateInstitutionAssignment,
            institutionId: institutionId,
        };
        setUpdateInstitutionAssignment(newUpdate);
        setAssignmentButtons(newUpdate);
    };

    return (
        <ContentArea>
            <ToolbarTop
                pageTitle={t("navigation.houseNumberInstitutionList")}
                errors={errors}
            >
            </ToolbarTop>
            <SearchCard
                onClickSearch={handleSearchClicked}
                onClickClearSearch={handleClearSearchClicked}
                isFilterChanged={filterChanged}
                hideDivider={true}
                cardTitle={t("common.schoolCandidateModel.schoolCandidateSearch")}
                textError={searchError}
            >
                <Row >
                    <Col md={6}>
                        <Select
                            multiple
                            label={t("common.schoolCandidateModel.countyList")}
                            name={nameof<typeof houseNumberInstitutionSearch>("countyList")}
                            value={houseNumberInstitutionSearch.countyList}
                            onChange={(e) => handleCountyChange(e.target.value)}
                            selectData={codeBookToSelectData(authCounties)}
                        />
                    </Col>
                    <Col md={6}>
                        <AutocompleteMultipleSelect<CodeBook>
                            label={t("common.schoolCandidateModel.institutionList")}
                            name={nameof<typeof houseNumberInstitutionSearch>("institution")}
                            value={houseNumberInstitutionSearch.institution !== undefined ? [houseNumberInstitutionSearch.institution] : []}
                            valueProp="id"
                            textProp="name"
                            onChange={handleInstitutionChange}
                            selectOptions={filteredInstitutions}
                            onFilterChange={handleInstitutionFilterChange}
                            maximumSelectedItems={1}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={6}>
                        <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.municipalityList")}
                            name={nameof<typeof houseNumberInstitutionSearch>("municipalityList")}
                            value={houseNumberInstitutionSearch.municipalityList}
                            valueProp="id"
                            textProp="name"
                            onChange={handleMunicipalityChange}
                            selectOptions={filteredMunicipalities}
                            onFilterChange={handleMunicipalityFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                    <Col md={6}>
                        <Select
                            label={t("common.houseNumberInstitution.assignmentStatus")}
                            name={nameof<typeof houseNumberInstitutionSearch>("assignmentStatus")}
                            value={houseNumberInstitutionSearch.assignmentStatus}
                            onChange={(e) => handleAssignedStatusChange(e.target.value)}
                            selectData={codeBookToSelectData(houseNumberAssignmentStatuses)}
                            emptyOption={{ text: t("common.all"), value: undefined }}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={6}>
                        <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.townList")}
                            name={nameof<typeof houseNumberInstitutionSearch>("townList")}
                            value={houseNumberInstitutionSearch.townList}
                            valueProp="id"
                            textProp="name"
                            onChange={handleTownChange}
                            selectOptions={filteredTowns}
                            onFilterChange={handleTownFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={6}>
                        <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.streetList")}
                            name={nameof<typeof houseNumberInstitutionSearch>("streetList")}
                            value={houseNumberInstitutionSearch.streetList}
                            valueProp="id"
                            textProp="name"
                            onChange={handleStreetChange}
                            selectOptions={filteredStreets}
                            onFilterChange={handleStreetFilterChange}
                            maximumSelectedItems={10}
                        />
                    </Col>
                </Row>
                <Row >
                    <Col md={2}>
                        <TextField
                            label={t("common.schoolCandidateModel.houseNumberFrom")}
                            name={nameof<typeof houseNumberInstitutionSearch>("houseNumberFrom")}
                            value={houseNumberInstitutionSearch.houseNumberFrom}
                            onChange={handleHouseNumberFromChange}
                        />
                    </Col>
                    <Col md={2}>
                        <TextField
                            label={t("common.schoolCandidateModel.houseNumberTo")}
                            name={nameof<typeof houseNumberInstitutionSearch>("houseNumberTo")}
                            value={houseNumberInstitutionSearch.houseNumberTo}
                            onChange={handleHouseNumberToChange}
                        />
                    </Col>
                    <Col md={2}>
                        <Select
                            label={t("common.schoolCandidateModel.houseNumberSelector")}
                            name={nameof<typeof houseNumberInstitutionSearch>("houseNumberSelector")}
                            value={houseNumberInstitutionSearch.houseNumberSelector}
                            onChange={handleHouseNumberSelectorChange}
                            selectData={codeBookToSelectData(houseNumberSelectors)}
                            emptyOption={{ value: "", text: t("common.all") }}
                        />
                    </Col>
                </Row>
            </SearchCard>
            <FormCard
                cardTitle={t("common.schoolCandidateModel.schoolCandidateAssign")}
            >
                <Row className="m-1 pt-3 bg-light border">
                    <Col md={2} className="pt-4 ">
                        <Checkbox
                            label={t("common.selectAll")}
                            name={"selectAll"}
                            checked={selectAll}
                            onChange={handleSelectAllCheckedChange}
                        />
                    </Col>
                    <Col md={6}>
                        <InstitutionSelect
                            label={t("common.schoolCandidateModel.institutionList")}
                            name={nameof<typeof updateInstitutionAssignment>("institutionId")}
                            value={updateInstitutionAssignment.institutionId}
                            onlyByEnrollmentArea={true}
                            excludeHandicapped={true}
                            excludePrivate={true}
                            excludeSatellite={false}
                            onChange={(e) => handleAssignInstitutionChange(e.target.value)}
                            countyIds={houseNumberInstitutionSearch.countyList}
                        />
                    </Col>
                    <Col md={2} className="pt-4">
                        <Button onClick={() => setConfirmUnassign("confirm")}
                            outline={true}
                            size="sm"
                            disabled={!clearAssignmentEnabled}>
                            {t("common.schoolCandidateModel.clearAssignments")}
                        </Button>
                    </Col>
                    <Col md={2} className="pt-4">
                        <Button onClick={handleAddAssignmentsClicked}
                            size="sm"
                            disabled={!addAssignmentEnabled}>
                            {t("common.schoolCandidateModel.addAssignments")}
                        </Button>
                    </Col>
                </Row>

            </FormCard>
            <Modal
                type={"message"}
                confirmText={t("common.close")}
                open={showSaveModal}
                onConfirm={handleCloseSaveConfirmation}
                onClose={handleCloseSaveConfirmation}
            >
                {t("common.saveSuccess")}
            </Modal>
            <Modal
                type={"confirm"}
                confirmText={t("common.save")}
                cancelText={t("common.cancel")}
                open={!!showConfirmModal}
                onConfirm={handleConfirmationClicked}
                onClose={() => setShowConfirmModal(undefined)}
            >
                {showConfirmModal}
            </Modal>
            <FormCard>
                <ListTable
                    tableProps={tableProps}
                    onClickDownload={(exportType: ExportTypeEnum) => handleDownloadClicked(exportType)}
                    showDownload
                    pageSizeOptions={[50, 100, 200, 300, 500, 1000, 1500, 2000, 6000]}
                />
            </FormCard>
            <ConfirmModal
                title={t("common.approve")}
                confirmState={confirmUnassign}
                confirmMessage={t("common.houseNumberInstitution.confirmUnassign")}
                successMesage={t("common.saveSuccess")}
                errorMessage={t("common.error")}
                onClickClose={() => setConfirmUnassign(undefined)}
                onClickConfirm={handleClearAssignmentsClicked}
            />
        </ContentArea>

    );
}